import { useState, useEffect } from 'react';

interface UserFiltersProps {
  initialValues?: UserFilterValues;
  onFiltersReady?: (getFilters: () => UserFilterValues) => void;
  onClear?: () => void;
}

export interface UserFilterValues {
  supplierName: string;
  userName: string;
  email: string;
  phoneNumber: string;
  role: string;
  status: string;
}

const defaultFilters: UserFilterValues = {
  supplierName: '',
  userName: '',
  email: '',
  phoneNumber: '',
  role: '',
  status: ''
};

export const UserFilters = ({ initialValues, onFiltersReady, onClear }: UserFiltersProps): JSX.Element => {
  const [filters, setFilters] = useState<UserFilterValues>(initialValues || defaultFilters);

  // Update local state when initialValues change
  useEffect(() => {
    setFilters(initialValues ?? defaultFilters);
  }, [initialValues]);

  // Provide a way for parent to get current filter values
  useEffect(() => {
    if (onFiltersReady) {
      onFiltersReady(() => filters);
    }
  }, [filters, onFiltersReady]);

  const handleChange = (field: keyof UserFilterValues, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="w-full" style={{ paddingTop: '24px' }}>
      <h2 style={{
        fontFamily: 'Oxygen',
        fontSize: '24px',
        fontWeight: 700,
        color: '#101828',
        marginBottom: '32px'
      }}>
        User
      </h2>

      <div className="space-y-6">
        {/* Supplier Name */}
        <div>
          <label style={{
            fontFamily: 'Oxygen',
            fontSize: '14px',
            fontWeight: 700,
            color: '#344054',
            display: 'block',
            marginBottom: '6px'
          }}>
            Supplier Name
          </label>
          <input
            type="text"
            placeholder="Search supplier"
            value={filters.supplierName}
            onChange={(e) => handleChange('supplierName', e.target.value)}
            style={{
              width: '100%',
              height: '40px',
              padding: '10px 14px',
              border: '1px solid #D0D5DD',
              borderRadius: '6px',
              fontFamily: 'Oxygen',
              fontSize: '14px',
              color: '#101828'
            }}
          />
        </div>

        {/* User Name */}
        <div>
          <label style={{
            fontFamily: 'Oxygen',
            fontSize: '14px',
            fontWeight: 700,
            color: '#344054',
            display: 'block',
            marginBottom: '6px'
          }}>
            User Name
          </label>
          <input
            type="text"
            placeholder="Search user name"
            value={filters.userName}
            onChange={(e) => handleChange('userName', e.target.value)}
            style={{
              width: '100%',
              height: '40px',
              padding: '10px 14px',
              border: '1px solid #D0D5DD',
              borderRadius: '6px',
              fontFamily: 'Oxygen',
              fontSize: '14px',
              color: '#101828'
            }}
          />
        </div>

        {/* Email */}
        <div>
          <label style={{
            fontFamily: 'Oxygen',
            fontSize: '14px',
            fontWeight: 700,
            color: '#344054',
            display: 'block',
            marginBottom: '6px'
          }}>
            Email
          </label>
          <input
            type="text"
            placeholder="Search email"
            value={filters.email}
            onChange={(e) => handleChange('email', e.target.value)}
            style={{
              width: '100%',
              height: '40px',
              padding: '10px 14px',
              border: '1px solid #D0D5DD',
              borderRadius: '6px',
              fontFamily: 'Oxygen',
              fontSize: '14px',
              color: '#101828'
            }}
          />
        </div>

        {/* Phone Number */}
        <div>
          <label style={{
            fontFamily: 'Oxygen',
            fontSize: '14px',
            fontWeight: 700,
            color: '#344054',
            display: 'block',
            marginBottom: '6px'
          }}>
            Phone Number
          </label>
          <input
            type="text"
            placeholder="Search phone number"
            value={filters.phoneNumber}
            onChange={(e) => handleChange('phoneNumber', e.target.value)}
            style={{
              width: '100%',
              height: '40px',
              padding: '10px 14px',
              border: '1px solid #D0D5DD',
              borderRadius: '6px',
              fontFamily: 'Oxygen',
              fontSize: '14px',
              color: '#101828'
            }}
          />
        </div>

        {/* Role */}
        <div>
          <label style={{
            fontFamily: 'Oxygen',
            fontSize: '14px',
            fontWeight: 700,
            color: '#344054',
            display: 'block',
            marginBottom: '6px'
          }}>
            Role
          </label>
          <select
            value={filters.role}
            onChange={(e) => handleChange('role', e.target.value)}
            style={{
              width: '100%',
              height: '40px',
              padding: '10px 14px',
              border: '1px solid #D0D5DD',
              borderRadius: '6px',
              fontFamily: 'Oxygen',
              fontSize: '14px',
              color: '#101828',
              backgroundColor: 'white'
            }}
          >
            <option value="">All Roles</option>
            <option value="superadmin">Super Admin</option>
            <option value="supplier_admin">Supplier Admin</option>
            <option value="supplier_user">Supplier User</option>
          </select>
        </div>

        {/* Status */}
        <div>
          <label style={{
            fontFamily: 'Oxygen',
            fontSize: '14px',
            fontWeight: 700,
            color: '#344054',
            display: 'block',
            marginBottom: '6px'
          }}>
            Status
          </label>
          <select
            value={filters.status}
            onChange={(e) => handleChange('status', e.target.value)}
            style={{
              width: '100%',
              height: '40px',
              padding: '10px 14px',
              border: '1px solid #D0D5DD',
              borderRadius: '6px',
              fontFamily: 'Oxygen',
              fontSize: '14px',
              color: '#101828',
              backgroundColor: 'white'
            }}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="pending_approval">Pending approval</option>
            <option value="rejected">Rejected</option>
            <option value="deleted">Deleted</option>
          </select>
        </div>
      </div>
    </div>
  );
};
